import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ImagePlus, Paperclip, Youtube, X, FileText, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* Extract 11-char YouTube video id from various URL forms. */
export function extractYoutubeId(url) {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

export default function AttachmentPicker({ attachments, setAttachments, testIdPrefix = 'attach' }) {
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showYoutube, setShowYoutube] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await axios.post(`${API}/upload-attachment`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachments(prev => [...prev, r.data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setUploading(false);
    }
  };

  const addYoutube = () => {
    const id = extractYoutubeId(youtubeUrl);
    if (!id) {
      toast.error('Geçerli bir YouTube bağlantısı girin');
      return;
    }
    setAttachments(prev => [...prev, {
      kind: 'youtube',
      url: `https://www.youtube.com/embed/${id}`,
      filename: youtubeUrl,
      content_type: 'video/youtube',
      size: 0,
    }]);
    setYoutubeUrl('');
    setShowYoutube(false);
  };

  const remove = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <input ref={imageInputRef} type="file" accept="image/*" className="hidden"
        onChange={e => { uploadFile(e.target.files[0]); e.target.value = ''; }} />
      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z,.mp3,.mp4,.mov,.webm" className="hidden"
        onChange={e => { uploadFile(e.target.files[0]); e.target.value = ''; }} />

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => imageInputRef.current?.click()} data-testid={`${testIdPrefix}-add-image`}>
          {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-1.5" />} Fotoğraf
        </Button>
        <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()} data-testid={`${testIdPrefix}-add-file`}>
          <Paperclip className="w-4 h-4 mr-1.5" /> Dosya
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => setShowYoutube(v => !v)} data-testid={`${testIdPrefix}-add-youtube`}>
          <Youtube className="w-4 h-4 mr-1.5 text-red-500" /> YouTube
        </Button>
      </div>

      {showYoutube && (
        <div className="flex gap-2 items-center bg-slate-50 p-2 rounded-lg border border-slate-200">
          <Youtube className="w-4 h-4 text-red-500 shrink-0" />
          <Input value={youtubeUrl} onChange={e => setYoutubeUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..." className="h-8" data-testid={`${testIdPrefix}-youtube-input`} />
          <Button type="button" size="sm" onClick={addYoutube} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid={`${testIdPrefix}-youtube-add-btn`}>Ekle</Button>
        </div>
      )}

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {attachments.map((a, i) => (
            <div key={i} data-testid={`${testIdPrefix}-item-${i}`}
              className="group flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg px-2 py-1 pr-1 text-xs max-w-[260px]">
              {a.kind === 'image' && (
                <img src={a.url.startsWith('http') ? a.url : `${API}/files/${a.url}`}
                  alt="" className="w-7 h-7 object-cover rounded" />
              )}
              {a.kind === 'youtube' && <Youtube className="w-4 h-4 text-red-500" />}
              {a.kind === 'file' && <FileText className="w-4 h-4 text-slate-500" />}
              <span className="truncate text-slate-700">{a.filename || a.url}</span>
              <button type="button" onClick={() => remove(i)}
                className="w-5 h-5 rounded hover:bg-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500"
                data-testid={`${testIdPrefix}-remove-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
